import {
  Box,
  Button,
  HStack,
  Stack,
  Text,
  Icon,
  useToast,
  useDisclosure,
  Wrap,
  WrapItem,
  Image,
  Select,
} from "@chakra-ui/react";
import { FaRuler, FaPhoneAlt, FaShareAlt } from "react-icons/fa";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosInstance";
import { loadStripe } from "@stripe/stripe-js";
import ShareModal from "../../components/ShareModal";
import PersonalizationModel from "../../components/Personalization/PersonalizationModel";
import ContactDrawer1 from "../productDetails/components/ContactDrawer1";
import NotifyCard from "../productDetails/components/NotifyCard";

// Icons
import Round from "./assets/Round.png";
import Square from "./assets/square.png";
import Cushion from "./assets/Cushion.png";
import Pier from "./assets/Pier.png";
import Radiant from "./assets/Radiant.png";
import Oval from "./assets/Oval.png";
import Platinum from "./assets/color/Platinum.png";
import White from "./assets/color/White.png";
import Rose from "./assets/color/Rose.png";
import Yellow from "./assets/color/Yellow.png";
import SEO from "../Blogs/Seo";

const BespokePurchaseCard = ({
  product,
  selectedShape,
  setSelectedShape,
  selectedSize,
  setSelectedSize,
  selectedColor,
  setSelectedColor,
  selectedClarity,
  setSelectedClarity,
  selectedGoldClarity,
  setSelectedGoldClarity,
  selectedGoldColor,
  setSelectedGoldColor,
  selectedVariant,
}) => {
  const [engraving, setEngraving] = useState("");
  const [birthStone, setBirthStone] = useState("");
  const [notify, setNotify] = useState(false);
  const [selectedJewelrySize, setSelectedJewelrySize] = useState("");
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    isOpen: isContactOpen,
    onOpen: onContactOpen,
    onClose: onContactClose,
  } = useDisclosure();
  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API_KEY);

  // Compute bespoke price from product.pricing and current selections
  const computedPrice = useMemo(() => {
    const pricing = product?.pricing || {};
    const calculationMode = pricing.calculationMode || {};
    const base = pricing.baseConfig || {};

    // Start from basePrice only
    let total = Number(pricing.basePrice || 0);

    // Gold karat adjustment: only difference from base
    const parseKarat = (val) => parseInt(String(val || "").replace(/\D/g, ""), 10);
    const selectedKarat = parseKarat(selectedGoldClarity);
    const baseKarat = parseKarat(base.goldKarat);
    const findKaratDelta = (k) => (pricing.goldKaratAdjustments || []).find((g) => Number(g.karat) === Number(k))?.delta || 0;
    if (!Number.isNaN(selectedKarat)) {
      const diffKarat = Number(findKaratDelta(selectedKarat)) - Number(findKaratDelta(baseKarat));
      total += diffKarat;
    }

    // Percentage from color and clarity should be applied relative to shape base
    const getPercent = (colorKey, clarityKey) => {
      const cPct = Number((pricing.colorAdjustments || []).find((c) => String(c.key).toUpperCase() === String(colorKey || "").toUpperCase())?.delta || 0);
      const qPct = Number((pricing.clarityAdjustments || []).find((c) => String(c.key).toUpperCase() === String(clarityKey || "").toUpperCase())?.delta || 0);
      return (cPct || 0) + (qPct || 0);
    };

    const currentShapeKey = String(selectedShape || base.stoneShape || "").toLowerCase();
    const baseShapeKey = String(base.stoneShape || "").toLowerCase();
    const shapeBaseSelected = Number((pricing.shapeAdjustments || []).find((s) => String(s.key).toLowerCase() === currentShapeKey)?.delta || 0);
    const shapeBaseBase = Number((pricing.shapeAdjustments || []).find((s) => String(s.key).toLowerCase() === baseShapeKey)?.delta || 0);

    const selectedPercent = getPercent(selectedColor || base.color, selectedClarity || base.clarity);
    const basePercent = getPercent(base.color, base.clarity);
    const percentDelta = selectedPercent - basePercent;
    total += shapeBaseSelected * (percentDelta / 100);

    // Apply shape base delta vs base shape so changing shape adjusts price
    total += (shapeBaseSelected - shapeBaseBase);

    // Diamond size (carat steps) difference from base
    const toNumber = (n) => Number(n);
    const selectedStep = Number(selectedSize) || toNumber(base.carat);
    const baseStep = toNumber(base.carat);
    const steps = pricing.caratStepAdjustments || [];
    const sumUpTo = (n) => steps.filter((s) => Number(s.step) <= Number(n)).reduce((sum, s) => sum + Number(s.delta || 0), 0);
    if (steps.length && Number.isFinite(selectedStep) && Number.isFinite(baseStep)) {
      if (calculationMode.carat === "cumulative") {
        total += sumUpTo(selectedStep) - sumUpTo(baseStep);
      } else {
        const selDelta = steps.find((s) => Number(s.step) === Number(selectedStep))?.delta || 0;
        const baseDelta = steps.find((s) => Number(s.step) === Number(baseStep))?.delta || 0;
        total += Number(selDelta) - Number(baseDelta);
      }
    }

    // Jewelry size adjustments difference from base (if any base defined, typically "1")
    const sizeRows = pricing.sizeAdjustments || [];
    if (sizeRows.length) {
      const sizeMode = calculationMode.size || "direct";
      const selectedJ = (selectedJewelrySize || "").toString();
      const baseJ = (sizeRows[0]?.size || "").toString();
      const getSizeValue = (val) => sizeRows.find((r) => String(r.size) === String(val))?.delta || 0;
      if (selectedJ) {
        if (sizeMode === "cumulative") {
          const toPairs = () =>
            sizeRows
              .map((r) => ({ num: Number(r.size), delta: Number(r.delta || 0) }))
              .filter((r) => Number.isFinite(r.num))
              .sort((a, b) => a.num - b.num);
          const pairs = toPairs();
          const sumCumu = (limit) => pairs.filter((p) => p.num <= Number(limit)).reduce((s, p) => s + p.delta, 0);
          total += sumCumu(selectedJ) - sumCumu(baseJ);
        } else {
          total += Number(getSizeValue(selectedJ)) - Number(getSizeValue(baseJ));
        }
      }
    }

    return Math.max(0, Math.round(total));
  }, [
    product?.pricing,
    selectedGoldClarity,
    selectedShape,
    selectedColor,
    selectedClarity,
    selectedSize,
    selectedJewelrySize,
  ]);

  const price = computedPrice;
  // const stock = selectedVariant?.stock || false;
  const variantImage = selectedVariant?.images?.[0] || product.images?.[0];
  const seo = product?.seo || {};
  const { metaTitle, metaDescription, metaKeywords, ogTitle, ogDescription } =
    seo;
  console.log(product.seo, "seoData");
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const showLoginToast = () => {
    toast({
      status: "warning",
      title: "Login Required",
      description: "Please login to continue.",
      isClosable: true,
      duration: 3000,
    });
  };

  const handleError = (
    error,
    defaultMessage = "Something went wrong. Try again."
  ) => {
    console.error(error);
    toast({
      status: "error",
      title: "Error",
      description: error.response?.data?.error || defaultMessage,
      isClosable: true,
      duration: 3000,
    });
  };

  const handleBuyNow = async () => {
    const token = localStorage.getItem("token");
    if (!token) return showLoginToast();

    try {
      const userData = localStorage.getItem("me");
      const { id: userId } = JSON.parse(userData) || {};

      const orderResponse = await axiosInstance.post(
        "/api/orders",
        {
          userId,
          items: [
            {
              productId: product._id,
              name: product.name,
              quantity: 1,
              price,
              image: variantImage,
              metalType: selectedColor,
              goldPurity: selectedGoldClarity,
              goldColor: selectedGoldColor,
              clarity: selectedClarity,
              size: selectedSize,
              jewelrySize: selectedJewelrySize,
              personalization: { engraving, birthStone },
            },
          ],
          amount: price,
          shippingAddress: {
            name: "Test User",
            addressLine1: "123 Main Street",
            addressLine2: "Apt 101",
            city: "Delhi",
            state: "Delhi",
            postalCode: "110001",
            country: "India",
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const sessionResponse = await axiosInstance.post(
        "/api/orders/create-checkout-session",
        { orderId: orderResponse.data._id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const stripe = await stripePromise;
      const sessionId = sessionResponse.data.sessionUrl
        .split("/c/pay/")[1]
        .split("#")[0];
      await stripe.redirectToCheckout({ sessionId });
    } catch (error) {
      handleError(error, "Checkout failed");
    }
  };

  const handleAddToBag = async () => {
    const token = localStorage.getItem("token");
    if (!token) return showLoginToast();

    try {
      const response = await axiosInstance.post(
        `/api/wishlist`,
        {
          productId: product._id,
          name: product.name,
          quantity: 1,
          price,
          image: variantImage,
          metalType: selectedColor,
          goldPurity: selectedGoldClarity,
          goldColor: selectedGoldColor,
          clarity: selectedClarity,
          size: selectedSize,
          jewelrySize: selectedJewelrySize,
          personalization: { engraving, birthStone },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const successToast = {
        position: "top-right",
        status: "success",
        duration: 2000,
        render: ({ onClose }) => (
          <Box color="black" p={3} bg="white" borderWidth="1px">
            <Box ml="auto" size="sm" color="#AB7D00" onClick={onClose} />
            <Text fontWeight="semibold" fontSize="lg">
              {response.data.status === "Product already in wishlist"
                ? "Already in your bag"
                : "Item added to your shopping bag"}
            </Text>
            <Text fontWeight="light" fontSize="sm" maxW="95%">
              Your creation has been added to your shopping bag
            </Text>
            <Button
              onClick={() => navigate("/shopping-bag")}
              bg="#022D2D"
              color="white"
              mt={5}
              variant="unstyled"
              px={4}
            >
              GO TO CHECKOUT
            </Button>
          </Box>
        ),
        isClosable: true,
      };

      toast(successToast);
    } catch (error) {
      handleError(error, "Failed to add to bag");
    }
  };

  const shapeImages = { Round, Square, Cushion, Pier, Radiant, Oval };
  const goldColorImages = { White, Yellow, Rose, Platinum };

  const infoLinks = [
    {
      icon: FaRuler,
      text: "Know Your Size",
      onClick: () => navigate("/jewellery-care"),
    },
    {
      icon: FaPhoneAlt,
      text: "Contact Us",
      onClick: () => navigate("/message"),
    },
    { icon: FaShareAlt, text: "Share", onClick: onOpen },
  ];

  // Determine if current product is ring or earring
  const isRingOrEarring = useMemo(() => {
    const collections = (product?.collection || []).map((c) => String(c).toLowerCase());
    return collections.includes("rings") || collections.includes("ring") || collections.includes("earrings") || collections.includes("earing");
  }, [product?.collection]);

  // Options for size dropdown
  const ringSizeOptions = useMemo(() => {
    // Common ring sizes (US) from 3 to 13 in 0.5 steps
    const sizes = [];
    for (let s = 3; s <= 13; s += 0.5) sizes.push(Number(s.toFixed(1)).toString());
    return sizes;
  }, []);

  const earringSizeOptions = useMemo(() => {
    // Hoop/earring diameters in mm
    return ["6", "8", "10", "12", "14", "16"]; // mm
  }, []);

  const jewelrySizeOptions = useMemo(() => {
    if (!isRingOrEarring) return [];
    const collections = (product?.collection || []).map((c) => String(c).toLowerCase());
    if (collections.includes("rings") || collections.includes("ring")) return ringSizeOptions;
    return earringSizeOptions;
  }, [isRingOrEarring, product?.collection, ringSizeOptions, earringSizeOptions]);

  // Initialize default jewelry size when applicable
  useEffect(() => {
    if (isRingOrEarring && jewelrySizeOptions.length > 0 && !selectedJewelrySize) {
      setSelectedJewelrySize(jewelrySizeOptions[0]);
    }
  }, [isRingOrEarring, jewelrySizeOptions, selectedJewelrySize]);

  return (
    <>
      <SEO
        pageTitle={metaTitle}
        canonicalUrl={`https://rrumidiamond.co/bespoke/product/${product._id}`}
        ogDescription={ogDescription}
        ogTitle={ogTitle}
        metaDescription={metaDescription}
        metaKeywords={metaKeywords}
        ogImage={variantImage}
        ogType="Product"
      />
      {notify ? (
        <NotifyCard onClose={() => setNotify(false)} />
      ) : (
      <Stack spacing={6}>
        {product?.name && (
          <Text fontSize="2xl" fontWeight="bold">
            {product.name}
          </Text>
        )}

        {/* Gold Details */}
        <Box>
          <Text fontWeight="medium">Gold Purity</Text>
          <HStack wrap="wrap">
            {(product?.goldclarity || []).map((option) => (
              <Button
                key={option}
                variant={selectedGoldClarity === option ? "solid" : "outline"}
                onClick={() => setSelectedGoldClarity(option)}
              >
                {option}
              </Button>
            ))}
          </HStack>
        </Box>

        <Box>
          <Text fontWeight="medium">Gold Color</Text>
          <Wrap>
            {(product?.goldcolor || []).map((option) => (
              <WrapItem key={option}>
                <Button
                  variant={selectedGoldColor === option ? "solid" : "outline"}
                  onClick={() => setSelectedGoldColor(option)}
                >
                  <Image
                    boxSize="30px"
                    src={goldColorImages[option]}
                    alt={option}
                    mr={2}
                  />
                  {option}
                </Button>
              </WrapItem>
            ))}
          </Wrap>
        </Box>

        {isRingOrEarring && (
          <Box>
            <Text fontWeight="medium" mt={4}>Size</Text>
            <Select
              value={selectedJewelrySize}
              onChange={(e) => setSelectedJewelrySize(e.target.value)}
              placeholder="Select size"
              maxW="240px"
            >
              {jewelrySizeOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          </Box>
        )}

        {/* Diamond Details */}
        <Box>
          <Text fontWeight="medium">Diamond Details</Text>

          <Box py={4}>
            <Text >Shape</Text>
            <Wrap>
              {Object.entries(shapeImages).map(([name, img]) => (
                <WrapItem key={name}>
                  <Button
                    onClick={() => setSelectedShape(name.toLowerCase())}
                    variant={
                      selectedShape === name.toLowerCase() ? "solid" : "outline"
                    }
                  >
                    <Image boxSize="40px" src={img} alt={name} mr={2} />
                    {name}
                  </Button>
                </WrapItem>
              ))}
            </Wrap>
          </Box>

          <Box py={4}>
            <Text fontWeight="medium">Diamond Size</Text>
            <HStack wrap="wrap">
              {(product?.pricing?.caratStepAdjustments || [])
                .map((s) => String(s.step))
                .map((size) => (
                  <Button
                    key={size}
                    variant={selectedSize === size ? "solid" : "outline"}
                    onClick={() => setSelectedSize(size)}
                  >
                    {size}
                  </Button>
                ))}
            </HStack>
          </Box>
          <Box py={4}>
            <Text>Color</Text>
            <HStack wrap="wrap">
              {["E", "F"].map((color) => (
                <Button
                  key={color}
                  variant={selectedColor === color ? "solid" : "outline"}
                  onClick={() => setSelectedColor(color)}
                >
                  {color}
                </Button>
              ))}
            </HStack>
          </Box>

          <Box py={4}>
            <Text>Clarity</Text>
            <HStack wrap="wrap">
              {["VVS2", "VS1", "VS2"].map((clarity) => (
                <Button
                  key={clarity}
                  variant={selectedClarity === clarity ? "solid" : "outline"}
                  onClick={() => setSelectedClarity(clarity)}
                >
                  {clarity}
                </Button>
              ))}
            </HStack>
          </Box>
        </Box>

        <HStack py={4} justify="space-between">
          <Text fontSize="2xl" fontWeight="bold">
            ₹ {price}
          </Text>
          {/* <Text color={stock ? "green.500" : "red.500"} fontWeight="medium">
            {stock ? "In Stock" : "Out of Stock"}
          </Text> */}
        </HStack>

        <Stack spacing={4}>
          <Button
            bg="black"
            color="white"
            _hover={{ bg: "black" }}
            size="lg"
            onClick={handleBuyNow}
          >
            Buy Now
          </Button>
          <Button variant="outline" size="lg" onClick={handleAddToBag}>
            Add to Bag
          </Button>

          <HStack spacing={4}>
            <Button variant="outline" flex={1} onClick={openModal}>
              Personalize It
            </Button>
            <Button variant="outline" flex={1} onClick={onContactOpen}>
              Contact Expert
            </Button>
          </HStack>
        </Stack>

        <Stack spacing={3}>
          {infoLinks.map(({ icon, text, onClick }) => (
            <HStack key={text} spacing={2} cursor="pointer" onClick={onClick}>
              <Icon as={icon} color="gold" />
              <Text fontSize="sm" fontWeight="medium">
                {text}
              </Text>
            </HStack>
          ))}
        </Stack>

        <ShareModal isOpen={isOpen} onClose={onClose} product={product} />
        <PersonalizationModel
          isOpen={isModalOpen}
          onClose={closeModal}
          engraving={engraving}
          birthstone={birthStone}
          onChangeEngraving={setEngraving}
          onChangeBirthstone={setBirthStone}
        />
        <ContactDrawer1
          isOpen={isContactOpen}
          onClose={onContactClose}
          productId={product._id}
        />
      </Stack>
      )}
    </>
  );
};

export default BespokePurchaseCard;
